import React, { createContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { fetchCustomerGroups, getCategoryByKey } from '../../features/home/services/eventApi';
import { fetchEventConfiguration } from '../../features/home/services/eventConfig';
import type { EventConfiguration, CustomerGroup } from '../../features/home/types/home.types';
import { CACHE_TTL_MS } from '../../shared/constants/cacheConstants';
import { NON_EVENT_PATH_SEGMENTS, EVENT_KEY_PATH_INDEX } from '../../shared/constants/event.constants';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EventContextType {
    eventData: EventConfiguration | null;
    customerGroups: CustomerGroup[];
    loading: boolean;
    error: Error | null;
    refetchEventData: (forceRefresh?: boolean) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const EventContext = createContext<EventContextType | undefined>(undefined);

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_CATEGORY_ID = import.meta.env.VITE_CATEGORY_ID as string | undefined;

// ─── Provider ─────────────────────────────────────────────────────────────────

export const EventProvider = ({ children }: { children: ReactNode }) => {
    const [eventData, setEventData] = useState<EventConfiguration | null>(null);
    const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    const lastFetchedAt = useRef<number | null>(null);
    const isFetching = useRef<boolean>(false);

    // ── Data Fetching ────────────────────────────────────────────────────────

    const fetchData = useCallback(async (forceRefresh = false, categoryId?: string) => {
        const targetId = categoryId ?? DEFAULT_CATEGORY_ID;

        if (!targetId) {
            setLoading(false);
            setError(new Error('No category ID available for event configuration.'));
            return;
        }

        if (isFetching.current && !forceRefresh) return;

        const now = Date.now();
        if (!forceRefresh && lastFetchedAt.current && (now - lastFetchedAt.current) < CACHE_TTL_MS) {
            return;
        }

        isFetching.current = true;

        // Show loading indicator only on first load or event switch
        if (!lastFetchedAt.current || categoryId) {
            setLoading(true);
        }

        try {
            const [eventConfig, groupsResponse] = await Promise.all([
                fetchEventConfiguration(targetId),
                fetchCustomerGroups(),
            ]);

            setEventData(eventConfig);
            setCustomerGroups(
                groupsResponse && Array.isArray(groupsResponse.results)
                    ? groupsResponse.results
                    : []
            );
            lastFetchedAt.current = Date.now();
            setError(null);
        } catch (err: unknown) {
            console.error('[EventContext] Failed to fetch event configuration:', err);
            setError(err instanceof Error ? err : new Error('Failed to fetch event data'));
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    }, []);

    // ── URL-based Category Resolution ────────────────────────────────────────

    const location = useLocation();
    const [resolvedCategoryId, setResolvedCategoryId] = useState<string | null>(null);

    useEffect(() => {
        let active = true;

        const resolveCategory = async () => {
            const pathParts = location.pathname.split('/').filter(Boolean);
            const candidateKey = pathParts[EVENT_KEY_PATH_INDEX];

            const isEventKey =
                candidateKey !== undefined &&
                !(NON_EVENT_PATH_SEGMENTS as readonly string[]).includes(candidateKey);

            if (isEventKey) {
                const category = await getCategoryByKey(candidateKey);
                if (!active) return;
                if (category) {
                    setResolvedCategoryId(category.id);
                    return;
                }
            }

            if (active) {
                setResolvedCategoryId(DEFAULT_CATEGORY_ID ?? null);
            }
        };

        resolveCategory();
        return () => { active = false; };
    }, [location.pathname]);

    // Trigger a fresh fetch whenever the resolved category changes
    useEffect(() => {
        if (!resolvedCategoryId) return;
        lastFetchedAt.current = null; // Bypass cache on event switch
        fetchData(true, resolvedCategoryId);
    }, [resolvedCategoryId, fetchData]);

    // ── Public API ───────────────────────────────────────────────────────────

    /**
     * Manually trigger a configuration refresh.
     * Respects cache TTL unless forceRefresh = true.
     * Useful when entering checkout or other pages that need fresh data.
     */
    const refetchEventData = useCallback((forceRefresh = false) => {
        fetchData(forceRefresh, resolvedCategoryId ?? undefined);
    }, [fetchData, resolvedCategoryId]);

    return (
        <EventContext.Provider value={{ eventData, customerGroups, loading, error, refetchEventData }}>
            {children}
        </EventContext.Provider>
    );
};

// useEventContext is exported from ./useEventContext to satisfy react-refresh/only-export-components

export default EventContext;
